import { createGameSession } from './gameSession.js';
import { calculateStars } from './progression.js';

const ART_W = 941;
const ART_H = 1672;

const ICON_URLS = Object.freeze({
  coin: new URL('../../assets/ui/icons/coin.svg', import.meta.url).href,
  gem: new URL('../../assets/ui/icons/gem.svg', import.meta.url).href,
  magnet: new URL('../../assets/ui/icons/magnet.svg', import.meta.url).href,
  hammer: new URL('../../assets/ui/icons/hammer.svg', import.meta.url).href,
  shuffle: new URL('../../assets/ui/icons/shuffle.svg', import.meta.url).href,
  pause: new URL('../../assets/ui/icons/pause.svg', import.meta.url).href,
});

const GAMEPLAY_REFERENCE = new URL('../../assets/concept/bolt-forge-mockup-04.png', import.meta.url).href;

function pct(value, total) {
  return `${(value / total) * 100}%`;
}

function el(tag, className, attrs = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  Object.entries(attrs).forEach(([key, value]) => {
    if (key === 'text') node.textContent = value;
    else if (key === 'html') node.innerHTML = value;
    else if (key === 'style') Object.assign(node.style, value);
    else node.setAttribute(key, value);
  });
  return node;
}

function panelTitle(label, value, modifier = '') {
  const panel = el('section', `bf-hud-panel ${modifier}`.trim());
  panel.append(el('span', 'bf-hud-panel__label', { text: label }), el('strong', 'bf-hud-panel__value', { text: value }));
  return panel;
}

function currency(kind, value) {
  const box = el('div', `bf-currency bf-currency--${kind}`);
  box.append(el('img', 'bf-currency__icon', { src: ICON_URLS[kind], alt: '' }), el('strong', 'bf-currency__value', { text: value }), el('button', 'bf-plus', { type: 'button', 'aria-label': `Add ${kind}`, text: '+' }));
  return box;
}

function buildHud(level, state, onPause) {
  const hud = el('header', 'bf-hud');
  const levelPanel = panelTitle('LEVEL', String(state.displayNumber), 'bf-hud-panel--level');
  levelPanel.querySelector('.bf-hud-panel__value').innerHTML = `<span class="bf-level-word">${state.displayNumber}</span>`;
  levelPanel.append(el('span', 'bf-hud-panel__sub', { text: level.objective || 'Clear all bolts' }));

  const moves = panelTitle('MOVES', String(state.movesRemaining), 'bf-hud-panel--moves');
  moves.querySelector('.bf-hud-panel__value').dataset.movesValue = 'true';

  const pause = el('button', 'bf-pause', { type: 'button', 'aria-label': 'Pause game' });
  pause.append(el('img', '', { src: ICON_URLS.pause, alt: '' }));
  pause.addEventListener('click', onPause);

  hud.append(levelPanel, moves, currency('coin', '12,480'), currency('gem', '320'), pause);
  return hud;
}

function buildAtmosphere() {
  const atmosphere = el('div', 'bf-atmosphere', { 'aria-hidden': 'true' });
  const concept = el('div', 'bf-atmosphere__concept');
  concept.style.backgroundImage = `url("${GAMEPLAY_REFERENCE}")`;
  atmosphere.append(
    concept,
    el('div', 'bf-atmosphere__wash'),
    el('div', 'bf-lamp bf-lamp--left'),
    el('div', 'bf-pipe-bank'),
    el('div', 'bf-workbench'),
    el('div', 'bf-neon', { html: 'SOLVE<br>UNSCREW<br>BUILD<br><span>A BRIGHTER<br>MIND ☺</span>' }),
    el('div', 'bf-wall-sign', { html: '<span>SMALL<br>PARTS</span><strong>BIG<br>IDEAS</strong>' }),
  );
  return atmosphere;
}

function createBar(object) {
  const bar = el('div', `bf-piece bf-piece--${object.kind || 'bar'} bf-metal--${object.color || 'steel'}`, {
    'data-object-id': object.id,
    'aria-hidden': 'true',
  });
  Object.assign(bar.style, {
    left: pct(object.x, ART_W),
    top: pct(object.y, ART_H),
    width: pct(object.w, ART_W),
    height: pct(object.h, ART_H),
    transform: `rotate(${object.rotation || 0}deg)`,
    zIndex: String(object.depth || 1),
  });
  if (object.radius) bar.style.borderRadius = object.radius;
  return bar;
}

function createBolt(bolt, session, onBolt) {
  const interactive = bolt.interactive !== false;
  const button = el('button', `bf-bolt bf-bolt--${bolt.color}${interactive ? '' : ' bf-bolt--anchor'}`, {
    type: 'button',
    'data-bolt-id': bolt.id,
    'data-color': bolt.color,
    'aria-label': interactive ? `Unscrew ${bolt.color} bolt` : `${bolt.color} fixed anchor`,
  });
  Object.assign(button.style, {
    left: pct(bolt.x, ART_W),
    top: pct(bolt.y, ART_H),
    zIndex: String(bolt.depth || 20),
  });
  button.innerHTML = '<span class="bf-bolt__rim"><span class="bf-bolt__face"><span class="bf-bolt__cross"></span></span></span>';

  if (!interactive) {
    button.disabled = true;
  } else {
    button.addEventListener('click', () => onBolt(bolt, button));
    if (!session.isBoltVisible(bolt.id)) {
      button.classList.add('is-engine-concealed', 'is-removed');
      button.disabled = true;
      button.setAttribute('aria-hidden', 'true');
    }
  }
  return button;
}

function buildBoard(level, session, onBolt) {
  const shell = el('section', 'bf-board-shell', { 'aria-label': `Bolt puzzle board for level ${level.displayNumber}` });
  shell.append(el('div', 'bf-backplate'));
  level.objects.forEach((object) => shell.append(createBar(object)));
  level.bolts.forEach((bolt) => shell.append(createBolt(bolt, session, onBolt)));
  return shell;
}

function buildBins(level) {
  const row = el('section', 'bf-bin-row', { 'aria-label': 'Bolt collection bins' });
  for (const target of level.storageTargets) {
    const { color, capacity } = target;
    const bin = el('div', `bf-bin bf-bin--${color}`, { 'data-bin': color });
    const contents = el('div', 'bf-bin__contents');
    for (let i = 0; i < capacity; i += 1) contents.append(el('span', `bf-mini-bolt bf-mini-bolt--${color}`));
    const label = el('div', 'bf-bin__label', { html: `<strong data-bin-count="${color}">0</strong><span>/${capacity}</span>` });
    bin.append(contents, label);
    row.append(bin);
  }
  return row;
}

function boosterLabel(id) {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

function buildBoosters(level, state, onClick) {
  const row = el('section', 'bf-booster-row', { 'aria-label': 'Boosters' });
  for (const booster of level.boosters) {
    const id = booster.id;
    const label = boosterLabel(id);
    const button = el('button', 'bf-booster', { type: 'button', 'data-booster': id, 'aria-label': `${label} booster` });
    button.append(
      el('span', 'bf-booster__badge', { text: String(state.boosters[id] ?? 0) }),
      el('img', 'bf-booster__icon', { src: ICON_URLS[id], alt: '' }),
      el('strong', 'bf-booster__name', { text: label.toUpperCase() }),
    );
    button.addEventListener('click', () => onClick(id, button));
    row.append(button);
  }
  return row;
}

function createModal() {
  const modal = el('div', 'bf-modal', { hidden: 'hidden' });
  modal.innerHTML = `
    <section class="bf-modal__panel" role="dialog" aria-modal="true" aria-labelledby="bf-modal-title">
      <p class="bf-modal__eyebrow">Bolt Forge</p>
      <h2 id="bf-modal-title">Paused</h2>
      <div class="bf-modal__stars" data-modal-stars hidden aria-label="Result stars">
        <span>★</span><span>★</span><span>★</span>
      </div>
      <p data-modal-copy>Take your next move when you are ready.</p>
      <div class="bf-modal__actions">
        <button type="button" data-modal-primary>Resume</button>
        <button type="button" data-modal-map>Map</button>
        <button type="button" data-modal-secondary>Restart</button>
      </div>
    </section>`;
  return modal;
}

function animateBoltRemoval(button, effect = '') {
  if (!button || button.classList.contains('is-removing') || button.classList.contains('is-removed')) return;
  button.classList.add('is-removing');
  if (effect) button.classList.add(`effect-${effect}`);
  window.setTimeout(() => button.classList.add('is-removed'), 470);
}

export function mountGameplay(root, level, {
  debugReference = false,
  onComplete = null,
  onNextLevel = null,
  onBackToMap = null,
  onRestart = null,
  rng = Math.random,
} = {}) {
  const session = createGameSession(level, { rng });
  let resultRecorded = false;

  root.replaceChildren();
  const viewport = el('main', 'bf-viewport');
  const stage = el('div', 'bf-stage', { 'data-level-id': level.id, 'data-level-number': String(level.displayNumber) });
  const isForgeChallenge = level.challenge?.type === 'forge-challenge';
  if (isForgeChallenge) {
    stage.classList.add('bf-stage--forge-challenge');
    stage.dataset.challenge = 'forge';
  }
  const modal = createModal();

  function state() { return session.snapshot(); }

  function updateHud() {
    const current = state();
    stage.querySelector('[data-moves-value]').textContent = String(current.movesRemaining);

    for (const [color, binState] of Object.entries(current.bins)) {
      const countNode = stage.querySelector(`[data-bin-count="${color}"]`);
      if (countNode) countNode.textContent = String(binState.count);
      const bin = stage.querySelector(`[data-bin="${color}"]`);
      if (bin) {
        bin.dataset.count = String(binState.count);
        bin.classList.toggle('is-full', binState.count >= binState.capacity);
      }
    }

    for (const [name, count] of Object.entries(current.boosters)) {
      const button = stage.querySelector(`[data-booster="${name}"]`);
      if (!button) continue;
      button.querySelector('.bf-booster__badge').textContent = String(count);
      button.disabled = count <= 0 || current.status !== 'playing' || current.paused;
      button.classList.toggle('is-armed', current.mode === name);
    }
  }

  function applyDerivedVisuals(event) {
    for (const id of event?.newlyReleased || []) {
      stage.querySelector(`[data-object-id="${id}"]`)?.classList.add('is-released');
    }
    for (const id of event?.newlyVisible || []) {
      const bolt = stage.querySelector(`[data-bolt-id="${id}"]`);
      if (!bolt) continue;
      bolt.classList.remove('is-engine-concealed', 'is-parity-concealed', 'is-removed');
      bolt.disabled = false;
      bolt.removeAttribute('aria-hidden');
      bolt.classList.add('is-parity-revealed');
    }
  }

  function recordCompletion() {
    if (resultRecorded || state().status !== 'won') return;
    resultRecorded = true;
    onComplete?.(level, state());
  }

  function showResult() {
    const current = state();
    if (current.status === 'playing') return;
    recordCompletion();
    modal.hidden = false;
    modal.classList.add('is-open');
    const won = current.status === 'won';
    const starsNode = modal.querySelector('[data-modal-stars]');
    const movesUsed = Math.max(0, Number(level.moveLimit || 0) - Number(current.movesRemaining || 0));
    const stars = won ? calculateStars(level, movesUsed) : 0;

    starsNode.hidden = !won;
    [...starsNode.children].forEach((node, index) => node.classList.toggle('is-earned', index < stars));

    modal.querySelector('#bf-modal-title').textContent = won ? (isForgeChallenge ? 'Forge Challenge Cleared!' : 'Forge Cleared!') : 'Out of Moves';
    modal.querySelector('[data-modal-copy]').textContent = won
      ? `Level ${level.displayNumber} cleared in ${movesUsed} moves. ${stars} star${stars === 1 ? '' : 's'} earned.`
      : 'Restart the level and find a cleaner sequence.';
    modal.querySelector('[data-modal-primary]').textContent = won && onNextLevel ? 'Next Level' : won ? 'Play Again' : 'Try Again';
    modal.querySelector('[data-modal-map]').textContent = 'Level Map';
    modal.querySelector('[data-modal-secondary]').textContent = 'Restart';
  }

  function handleEngineEvent(event) {
    if (!event?.ok) return;
    applyDerivedVisuals(event);
    if (event.first) applyDerivedVisuals(event.first);
    if (event.second) applyDerivedVisuals(event.second);
    updateHud();
    showResult();
  }

  function onBolt(bolt, button) {
    const before = state();
    if (before.paused || before.status !== 'playing') return;

    const result = session.activateBolt(bolt.id);
    if (!result.ok) return;

    if (result.type === 'magnet-used') {
      for (const id of result.removedBoltIds) {
        animateBoltRemoval(stage.querySelector(`[data-bolt-id="${id}"]`), 'magnet');
      }
    } else {
      animateBoltRemoval(button, result.effect === 'normal' ? '' : result.effect);
    }
    handleEngineEvent(result);
  }

  function onBooster(name) {
    const current = state();
    if (current.paused || current.status !== 'playing' || (current.boosters[name] ?? 0) <= 0) return;

    if (name === 'shuffle') {
      const result = session.shuffleRemainingColors();
      if (!result.ok) return;
      for (const [id, nextColor] of Object.entries(result.assignments)) {
        const node = stage.querySelector(`[data-bolt-id="${id}"]`);
        if (!node) continue;
        const old = node.dataset.color;
        node.dataset.color = nextColor;
        node.classList.remove(`bf-bolt--${old}`);
        node.classList.add(`bf-bolt--${nextColor}`, 'is-shuffling');
        window.setTimeout(() => node.classList.remove('is-shuffling'), 520);
      }
      handleEngineEvent(result);
      return;
    }

    session.armBooster(name);
    updateHud();
  }

  function setPaused(value) {
    session.setPaused(value);
    const current = state();
    modal.hidden = !current.paused;
    modal.classList.toggle('is-open', current.paused);
    if (current.paused) {
      modal.querySelector('[data-modal-stars]').hidden = true;
      modal.querySelector('#bf-modal-title').textContent = 'Paused';
      modal.querySelector('[data-modal-copy]').textContent = `Level ${level.displayNumber}: ${level.title}`;
      modal.querySelector('[data-modal-primary]').textContent = 'Resume';
      modal.querySelector('[data-modal-map]').textContent = 'Level Map';
      modal.querySelector('[data-modal-secondary]').textContent = 'Restart';
    }
    updateHud();
  }

  function restart() {
    if (onRestart) onRestart(level);
    else mountGameplay(root, level, { debugReference, onComplete, onNextLevel, onBackToMap, onRestart, rng });
  }

  const initial = state();
  stage.append(
    buildAtmosphere(),
    buildHud(level, initial, () => setPaused(true)),
    buildBoard(level, session, onBolt),
    buildBins(level),
    buildBoosters(level, initial, onBooster),
    modal,
  );

  if (isForgeChallenge) {
    stage.append(el('div', 'bf-forge-challenge-banner', {
      html: '<span>🔥</span><strong>FORGE CHALLENGE</strong><small>MASTER CORE</small>',
      'aria-label': 'Forge Challenge',
    }));
  }

  modal.querySelector('[data-modal-primary]').addEventListener('click', () => {
    const current = state();
    if (current.status === 'won' && onNextLevel) onNextLevel(level, current);
    else if (current.paused) setPaused(false);
    else restart();
  });
  modal.querySelector('[data-modal-map]').addEventListener('click', () => {
    if (onBackToMap) onBackToMap(level, state());
  });
  modal.querySelector('[data-modal-secondary]').addEventListener('click', restart);

  if (debugReference) {
    const overlay = el('div', 'bf-reference-overlay', { 'aria-hidden': 'true' });
    overlay.style.backgroundImage = `url("${GAMEPLAY_REFERENCE}")`;
    stage.append(overlay);
    const toggle = el('button', 'bf-reference-toggle', { type: 'button', text: 'Reference 50%' });
    toggle.addEventListener('click', () => {
      overlay.classList.toggle('is-hidden');
      toggle.textContent = overlay.classList.contains('is-hidden') ? 'Reference off' : 'Reference 50%';
    });
    stage.append(toggle);
  }

  viewport.append(stage);
  root.append(viewport);
  updateHud();

  return Object.freeze({ session, stage, restart });
}
